import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import moment from 'moment';
import Api, { LOTTERY_WEBSITE_URL } from '../../../util/api';
import useConfig from '../../../util/useConfig';
import { formatCurrencyNumber } from '../../../util/functions';
import { partial } from '../../../util/translation/translation';

const p = partial('NewsForm');
// How many of each kind to pick from
const COUNT = 20;
const KINDS = ['match', 'event', 'lottery', 'offer'];

const when = (date) =>
  date ? moment(date).format('dddd D MMMM YYYY, HH:mm') : null;
const plainText = (html, length = 1500) => {
  const text = (html || '')
    .replace(/<\/(p|div|li|h\d)>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
};
const teamName = (match, side) =>
  match.isCustom ? match.extraData?.[side] : match[side]?.name;

// Each kind: how to list them (upcoming or running first), what to call one
// in the picker, its facts as [label key, value] and its image
const SOURCES = {
  match: {
    load: async (config) => {
      const res = await Api.matchCenter.getUpcomingMatches(
        1,
        COUNT,
        (config.teams || []).map((team) => team.id),
        false
      );
      return res.data?.matches || [];
    },
    label: (match) =>
      `${teamName(match, 'homeTeam')} – ${teamName(match, 'awayTeam')} · ${moment
        .utc(match.matchStart)
        .local()
        .format('DD.MM HH:mm')}`,
    facts: (match) => [
      [
        'teams',
        `${teamName(match, 'homeTeam')} – ${teamName(match, 'awayTeam')}`,
      ],
      ['kickoff', when(moment.utc(match.matchStart).local())],
      ['competition', match.league?.name || match.extraData?.matchType],
      ['tickets', match.settings?.settings?.ticket?.link],
      ['sponsor', match.settings?.settings?.sponsor?.name],
      ...(match.settings?.settings?.links || [])
        .filter((link) => link?.link)
        .map((link) => ['link', `${link.name || ''} ${link.link}`.trim()]),
    ],
    image: () => null,
  },
  event: {
    load: async () => {
      const res = await Api.events.all();
      return (res.data || [])
        .filter((event) => moment(event.endDate || event.startDate).isAfter())
        .sort((a, b) => moment(a.startDate).diff(moment(b.startDate)))
        .slice(0, COUNT);
    },
    label: (event) =>
      `${event.title} · ${moment(event.startDate).format('DD.MM HH:mm')}`,
    facts: (event) => [
      ['title', event.title],
      ['starts', when(event.startDate)],
      ['ends', when(event.endDate)],
      [
        'place',
        [event.address, [event.zip, event.city].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', '),
      ],
      ['map', event.mapsUrl],
      [
        'registration',
        event.registrationStart &&
          `${when(event.registrationStart)} – ${when(event.registrationEnd)}`,
      ],
      ['places', event.maxAttendees],
      ['category', event.category?.title],
      ['link', event.websiteUrl],
      ['booking', event.externalBooking && event.externalBookingUrl],
      ['appLink', event.dynamicLink],
      ['description', plainText(event.description)],
    ],
    image: (event) => event.imageUrl,
  },
  lottery: {
    load: async () => {
      const res = await Api.lottery.getLotteries(
        { page: 1, take: COUNT },
        { orderBy: 'createdAt', direction: 'DESC' }
      );
      return (res?.data || []).filter(
        (lottery) => !lottery.endDate || moment(lottery.endDate).isAfter()
      );
    },
    label: (lottery) => lottery.name,
    facts: (lottery, config) => [
      ['name', lottery.name],
      [
        'ticketPrice',
        lottery.ticketPrice &&
          `${formatCurrencyNumber(lottery.ticketPrice)} ${config.currencyCaps}`,
      ],
      ['starts', when(lottery.startDate)],
      ['ends', when(lottery.endDate)],
      [
        'winnersShare',
        lottery.winningSplitPercent && `${lottery.winningSplitPercent} %`,
      ],
      [
        'maxPrize',
        lottery.maxPrizePerWinner &&
          `${formatCurrencyNumber(lottery.maxPrizePerWinner)} ${
            config.currencyCaps
          }`,
      ],
      [
        'prizes',
        (lottery.lotteryPrizes || [])
          .map((prize) => prize?.name || prize?.title)
          .filter(Boolean)
          .join(', '),
      ],
      [
        'sponsor',
        [lottery.sponsorName, lottery.sponsorLink].filter(Boolean).join(' '),
      ],
      ['appLink', lottery.dynamicLink],
      ['countdown', `${LOTTERY_WEBSITE_URL}/lotteries/countdown/${lottery.id}`],
      ['description', plainText(lottery.description)],
    ],
    image: (lottery) => lottery.imageUrl,
  },
  offer: {
    load: async (config) => {
      const res = await Api.offers.fetchAllOffers(
        config.id,
        1,
        COUNT,
        'active'
      );
      return res.data?.offers || [];
    },
    label: (offer) => offer.title,
    facts: (offer, config) => [
      ['title', offer.title],
      ['subtitle', offer.subtitle],
      [
        'price',
        offer.price === 0
          ? p('aiFactFree')
          : offer.price &&
            `${formatCurrencyNumber(offer.price)} ${config.currencyCaps}`,
      ],
      [
        'discountedPrice',
        offer.discountedPrice &&
          `${formatCurrencyNumber(offer.discountedPrice)} ${
            config.currencyCaps
          }`,
      ],
      [
        'discount',
        offer.discountedPercentage && `${offer.discountedPercentage} %`,
      ],
      ['starts', when(offer.startDate)],
      ['ends', when(offer.endDate)],
      ['link', offer.promoType === 'link' && offer.websiteUrl],
      ['code', offer.promoType === 'code' && offer.promoCode],
      ['description', plainText(offer.description)],
    ],
    image: (offer) => offer.imageUrl,
  },
};

/**
 * Facts for the brief from the club's own data: a match, event, lottery or
 * offer is picked and its details written into the brief as a list the AI
 * is told to use as given, so they aren't typed (or mistyped) by hand. Its
 * image is offered to the AI too.
 */
const AiDraftFacts = ({ onAdd }) => {
  const config = useConfig();
  const [kind, setKind] = useState('');
  // The kind's items (null while they load)
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPicked('');
    setFailed(false);
    if (!kind) {
      setItems([]);
      return undefined;
    }
    let current = true;
    setItems(null);
    SOURCES[kind]
      .load(config)
      .then((loaded) => current && setItems(loaded))
      .catch(() => {
        if (!current) return;
        setItems([]);
        setFailed(true);
      });
    return () => {
      current = false;
    };
    // config is read as it is when a kind is picked
    // eslint-disable-next-line
  }, [kind]);

  const add = () => {
    const source = SOURCES[kind];
    const item = items.find((entry) => String(entry.id) === picked);
    if (!item) return;
    const lines = source
      .facts(item, config)
      .filter(
        ([, value]) =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== false
      )
      .map(([label, value]) =>
        label === 'description'
          ? `${p(`aiFact-${label}`)}:\n${value}`
          : `- ${p(`aiFact-${label}`)}: ${value}`
      );
    onAdd(
      `${p(`aiFactsHeading-${kind}`)}\n${lines.join('\n')}`,
      source.image(item)
    );
    setPicked('');
  };

  return (
    <div className="ai-draft__facts">
      <span className="form-header">{p('aiFacts')}</span>
      <div className="ai-draft__facts-row">
        <select
          className="ai-draft__select"
          aria-label={p('aiFacts')}
          value={kind}
          onChange={(event) => setKind(event.target.value)}
        >
          <option value="">{p('aiFactsChooseKind')}</option>
          {KINDS.map((option) => (
            <option key={option} value={option}>
              {p(`aiFactsKind-${option}`)}
            </option>
          ))}
        </select>
        {kind && (
          <select
            className="ai-draft__select"
            aria-label={p(`aiFactsKind-${kind}`)}
            value={picked}
            disabled={!items || items.length === 0}
            onChange={(event) => setPicked(event.target.value)}
          >
            <option value="">
              {items === null
                ? p('aiFactsLoading')
                : failed
                  ? p('aiFactsFailed')
                  : items.length === 0
                    ? p('aiFactsNone')
                    : p('aiFactsChoose')}
            </option>
            {(items || []).map((item) => (
              <option key={item.id} value={String(item.id)}>
                {SOURCES[kind].label(item)}
              </option>
            ))}
          </select>
        )}
        <Button
          type="button"
          size="sm"
          outline
          color="primary"
          className="mb-0"
          disabled={!picked}
          onClick={add}
        >
          {p('aiFactsAdd')}
        </Button>
      </div>
    </div>
  );
};

export default AiDraftFacts;
